import { Injectable } from '@angular/core';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { COMMISSIONING_STATUS_DEFINITIONS } from './definitions/commissioning.status-definitions';
import { CONNECTION_CHANGE_REQUEST_STATUS_DEFINITIONS } from './definitions/connection-change-request.status-definitions';
import { FEEDER_CONTRACT_STATUS_DEFINITIONS } from './definitions/feeder-contract.status-definitions';
import { GRID_REGISTRATION_STATUS_DEFINITIONS } from './definitions/grid-registration.status-definitions';
import { INSTALLER_PASS_STATUS_DEFINITIONS } from './definitions/installer-pass.status-definitions';
import { ORDER_REQUEST_STATUS_DEFINITIONS } from './definitions/order-request.status-definitions';
import { ORDERS_STATUS_DEFINITIONS } from './definitions/orders.status-definitions';
import { filterSelectable, withCurrentStatus } from './status-availability';
import { StatusAvailabilityContext } from './status-availability.model';
import {
  SelectableStatus,
  StatusCatalogKey,
  StatusDefinition,
  StatusModule,
  StatusType
} from './status.model';

/** Standardfarbe für Status ohne eigene Farbe. */
export const DEFAULT_STATUS_COLOR = '#c9c9c9';

/**
 * Kataloge, in denen ein Statuswert ohne bekanntes Modul nachgeschlagen wird – in dieser Reihenfolge
 * (erster Treffer gewinnt). Nur für Anzeigen, deren Modul nicht übergeben wird.
 */
export const REQUEST_STATUS_KEYS: readonly StatusCatalogKey[] = [
  'commissioning',
  'gridRegistration',
  'feederContract',
  'connectionChangeRequest'
];

const DEFINITIONS: Record<StatusCatalogKey, readonly StatusDefinition[]> = {
  commissioning: COMMISSIONING_STATUS_DEFINITIONS,
  gridRegistration: GRID_REGISTRATION_STATUS_DEFINITIONS,
  feederContract: FEEDER_CONTRACT_STATUS_DEFINITIONS,
  orderRequests: ORDER_REQUEST_STATUS_DEFINITIONS,
  orders: ORDERS_STATUS_DEFINITIONS,
  installerPassList: INSTALLER_PASS_STATUS_DEFINITIONS,
  connectionChangeRequest: CONNECTION_CHANGE_REQUEST_STATUS_DEFINITIONS
};

const toOption = (definition: StatusDefinition): StatusType =>
  Object.freeze({
    value: definition.value,
    displayName: definition.defaultLabel,
    cssColor: definition.defaultColor
  });

/**
 * Einzige Quelle für Status in Komponenten und Pipes: Werte, Bezeichnungen, Farben und die in Filter und
 * „Status ändern“ angebotenen Status je Modul. Die Konstanten in `definitions/` sind die Default-Werte;
 * mandantenspezifische Bezeichnungen und Farben (Work Item 5395) werden hier in `catalog` eingemischt.
 */
@Injectable({ providedIn: 'root' })
export class StatusService {
  private readonly catalog = new Map<StatusCatalogKey, readonly StatusType[]>(
    (Object.keys(DEFINITIONS) as StatusCatalogKey[]).map((key) => [
      key,
      Object.freeze(DEFINITIONS[key].map(toOption))
    ])
  );

  private readonly context$: Observable<StatusAvailabilityContext>;

  constructor(private readonly dataShareService: DataShareService) {
    this.context$ = combineLatest([
      this.dataShareService.tenantConfig$,
      this.dataShareService.uiAdaptions$,
      this.dataShareService.currentTenant$
    ]).pipe(
      map(([config, uiAdaptions, tenant]) => ({
        config: config ?? {},
        uiAdaptions: uiAdaptions ?? [],
        tenant: tenant ?? null
      })),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /** Alle Status eines Katalogs in Definitionsreihenfolge (unveränderlich). */
  statuses(key: StatusCatalogKey): readonly StatusType[] {
    return this.catalog.get(key) ?? [];
  }

  /** Wie {@link statuses}, reaktiv – ändert sich, sobald mandantenspezifische Werte greifen. */
  statuses$(key: StatusCatalogKey): Observable<readonly StatusType[]> {
    return of(this.statuses(key));
  }

  /** Alle Status eines Katalogs, alphabetisch nach deutscher Bezeichnung (neue Liste). */
  sortedStatuses(key: StatusCatalogKey): StatusType[] {
    return [...this.statuses(key)].sort(byDisplayName);
  }

  /** Status eines Katalogs; ohne Treffer `undefined`. `ignoreCase` für Werte aus Aktivitäten. */
  find(key: StatusCatalogKey, value: string | null | undefined, ignoreCase = false): StatusType | undefined {
    return value == null
      ? undefined
      : this.statuses(key).find((status) => sameValue(status.value, value, ignoreCase));
  }

  /** Status ohne bekanntes Modul: erster Treffer in `keys` (Default {@link REQUEST_STATUS_KEYS}). */
  findAcross(
    value: string | null | undefined,
    keys: readonly StatusCatalogKey[] = REQUEST_STATUS_KEYS,
    ignoreCase = false
  ): StatusType | undefined {
    for (const key of keys) {
      const status = this.find(key, value, ignoreCase);
      if (status) {
        return status;
      }
    }
    return undefined;
  }

  /** Bezeichnung; leerer Wert → `-`, unbekannter Wert → der Wert selbst. */
  label(key: StatusCatalogKey, value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    return this.find(key, value)?.displayName ?? value;
  }

  /** Farbe; ohne Treffer oder ohne eigene Farbe {@link DEFAULT_STATUS_COLOR}. */
  color(key: StatusCatalogKey, value: string | null | undefined): string {
    return this.find(key, value)?.cssColor || DEFAULT_STATUS_COLOR;
  }

  /**
   * In Statusfilter und „Status ändern“ angebotene Status. Ist
   * `statusManagement.<modul>.hideUnreachableStatuses` aus, sind es alle; ist sie an, fehlen die, die der
   * Mandant über keinen Prozessschritt erreicht (Regeln in `rules/`). Immer eine neue Liste.
   */
  selectable$(module: StatusModule): Observable<StatusType[]> {
    return this.context$.pipe(
      map((ctx) => filterSelectable(module, this.statuses(module), ctx)),
      distinctUntilChanged(sameOptions)
    );
  }

  /** Wie {@link selectable$}, alphabetisch nach deutscher Bezeichnung – für Auswahlfelder „Status ändern“. */
  sortedSelectable$(module: StatusModule): Observable<StatusType[]> {
    return this.selectable$(module).pipe(map((statuses) => [...statuses].sort(byDisplayName)));
  }

  /**
   * Set-Filter der Listen: wie {@link sortedSelectable$}. Der Filter braucht `suppressSorting: true`, sonst
   * sortiert ag-grid die Werte nach dem Statuscode statt nach der Bezeichnung.
   */
  filterOptions$(module: StatusModule): Observable<StatusType[]> {
    return this.sortedSelectable$(module);
  }

  /**
   * Für „Status ändern“: alphabetisch nach Bezeichnung. Steht der Vorgang in einem ausgeblendeten Status,
   * kommt dieser als deaktivierte, vorangestellte Option „(aktueller Status)“ dazu, damit die Auswahl nicht
   * leer wirkt.
   */
  selectableWithCurrent$(
    module: StatusModule,
    current: string | null | undefined
  ): Observable<SelectableStatus[]> {
    return this.sortedSelectable$(module).pipe(
      map((selectable) => withCurrentStatus(selectable, this.statuses(module), current))
    );
  }

  /** Synchrone Variante von {@link withCurrentStatus} für Templates (`withCurrentStatus`-Pipe). */
  withCurrent(
    module: StatusModule,
    selectable: readonly SelectableStatus[],
    current: string | null | undefined
  ): SelectableStatus[] {
    return withCurrentStatus(selectable, this.statuses(module), current);
  }
}

function sameValue(a: string, b: string, ignoreCase: boolean): boolean {
  return ignoreCase ? a.toLowerCase() === b.toLowerCase() : a === b;
}

const GERMAN_COLLATOR = new Intl.Collator('de', { sensitivity: 'base' });

/** Alphabetisch nach deutscher Bezeichnung (Umlaute wie im Wörterbuch). */
function byDisplayName(a: StatusType, b: StatusType): number {
  return GERMAN_COLLATOR.compare(a.displayName ?? '', b.displayName ?? '');
}

function sameOptions(previous: StatusType[], current: StatusType[]): boolean {
  return previous.length === current.length && previous.every((status, index) => status === current[index]);
}
